import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface Profile {
  id: string;
  user_type: 'breeder' | 'buyer' | 'admin';
  first_name?: string;
  last_name?: string;
  business_name?: string;
  location?: string;
  subscription_active?: boolean;
  admin_tier?: string;
}

interface Subscription {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_end?: string;
  payment_provider?: string;
  cancel_at_period_end?: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  subscription: Subscription | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshSubscription?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasRedirectedAfterLogin, setHasRedirectedAfterLogin] = useState(false);
  const userIdRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const fetchProfile = async (userId: string) => {
    try {
      console.log("AuthContext: Fetching profile for user:", userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      const profileData: Profile = {
        ...data,
        user_type: data.user_type as 'breeder' | 'buyer' | 'admin'
      };

      console.log("AuthContext: Setting profile data:", profileData);
      setProfile(profileData);
      return profileData;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  // Fix 2: Edge function call with 10s timeout + fallback to DB profile
  const checkSubscriptionFromStripe = async (currentUser: User) => {
    if (!currentUser) {
      return { profileData: null, subscriptionData: null };
    }

    try {
      console.log("AuthContext: Checking subscription from Stripe for user:", currentUser.email);

      // 10-second timeout for the edge function call
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Subscription check timed out')), 10000)
      );

      const invokePromise = supabase.functions.invoke('check-subscription');

      const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

      if (error) {
        console.error('Error checking subscription:', error);
        const profileData = await fetchProfile(currentUser.id);
        return {
          profileData,
          subscriptionData: {
            subscribed: profileData?.subscription_active || false,
            subscription_tier: profileData?.subscription_active ? 'Premium' : null,
            subscription_end: null,
            payment_provider: 'stripe',
            cancel_at_period_end: false,
          }
        };
      }

      console.log("AuthContext: Received subscription data from Stripe:", data);

      let subscriptionData: Subscription;
      if (data) {
        subscriptionData = {
          subscribed: Boolean(data.subscribed),
          subscription_tier: data.subscription_tier || null,
          subscription_end: data.subscription_end || null,
          payment_provider: 'stripe',
          cancel_at_period_end: Boolean(data.cancel_at_period_end),
        };

        console.log("AuthContext: Setting subscription state to:", subscriptionData);
        setSubscription(subscriptionData);
      } else {
        subscriptionData = {
          subscribed: false,
          subscription_tier: null,
          subscription_end: null,
          payment_provider: 'stripe',
          cancel_at_period_end: false,
        };
        setSubscription(subscriptionData);
      }

      const profileData = await fetchProfile(currentUser.id);
      return { profileData, subscriptionData };

    } catch (error) {
      // Timeout or network error — fall back to profile from DB
      console.warn('AuthContext: Subscription check failed, falling back to profile:', error);
      const profileData = await fetchProfile(currentUser.id);
      const subscriptionData: Subscription = {
        subscribed: profileData?.subscription_active || false,
        subscription_tier: profileData?.subscription_active ? 'Premium' : null,
        subscription_end: null,
        payment_provider: 'stripe',
        cancel_at_period_end: false,
      };
      setSubscription(subscriptionData);
      return { profileData, subscriptionData };
    }
  };

  const refreshSubscription = async () => {
    if (user) {
      await checkSubscriptionFromStripe(user);
    }
  };

  const handleDirectRedirect = (currentUser: User, event: string, profileData: Profile | null, currentSubscription: Subscription | null) => {
    if (event !== 'SIGNED_IN') return;

    console.log('AuthContext: Checking direct redirect for user:', currentUser.email, 'Event:', event);
    console.log('AuthContext: Profile data:', profileData);
    console.log('AuthContext: hasRedirectedAfterLogin:', hasRedirectedAfterLogin);

    const urlParams = new URLSearchParams(window.location.search);
    const isEmailVerification = urlParams.has('type') && urlParams.get('type') === 'signup';

    if (hasRedirectedAfterLogin && !isEmailVerification) {
      console.log('AuthContext: Already redirected this session, skipping redirect');
      return;
    }

    if (window.location.pathname === '/pricing') {
      console.log('AuthContext: Already on pricing page, skipping redirect');
      return;
    }

    console.log('AuthContext: Is email verification?', isEmailVerification);

    if (isEmailVerification) {
      if (profileData?.user_type === 'breeder') {
        setTimeout(() => { navigate('/pricing'); setHasRedirectedAfterLogin(true); }, 1000);
        return;
      } else if (profileData?.user_type === 'buyer') {
        setTimeout(() => { navigate('/buyer-dashboard'); setHasRedirectedAfterLogin(true); }, 1000);
        return;
      } else if (profileData?.user_type === 'admin') {
        setTimeout(() => { navigate('/admin-dashboard'); setHasRedirectedAfterLogin(true); }, 1000);
        return;
      }
    }

    if (profileData?.user_type === 'breeder') {
      const hasActiveAccess = currentSubscription?.subscribed ||
                             profileData?.subscription_active ||
                             (profileData?.admin_tier && profileData.admin_tier === 'freemium');

      if (!hasActiveAccess) {
        setTimeout(() => { navigate('/pricing'); setHasRedirectedAfterLogin(true); }, 1000);
      } else {
        setTimeout(() => { navigate('/breeder-dashboard'); setHasRedirectedAfterLogin(true); }, 1000);
      }
    } else if (profileData?.user_type === 'buyer') {
      setTimeout(() => { navigate('/buyer-dashboard'); setHasRedirectedAfterLogin(true); }, 1000);
    } else if (profileData?.user_type === 'admin') {
      setTimeout(() => { navigate('/admin-dashboard'); setHasRedirectedAfterLogin(true); }, 1000);
    }
  };

  useEffect(() => {
    console.log("AuthContext: useEffect running - initializing auth");
    let mounted = true;

    // Fix 5: Safety timeout — forces loading to false after 15 seconds
    const safetyTimeout = setTimeout(() => {
      if (mounted) {
        console.warn('AuthContext: Safety timeout - forced loading to false');
        setLoading(false);
      }
    }, 15000);

    // Fix 1: Register listener FIRST to avoid race condition
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log("AuthContext: Auth state change:", event);
      const currentUser = session?.user ?? null;

      if (currentUser) {
        // Fix 3: Handle both SIGNED_IN and TOKEN_REFRESHED as token refreshes
        const isTokenRefresh = (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')
          && userIdRef.current === currentUser.id;

        if (isTokenRefresh) {
          console.log("AuthContext: Token refresh detected, skipping subscription check");
          setLoading(false);
          return;
        }

        // Genuine new sign-in
        setUser(currentUser);
        userIdRef.current = currentUser.id;

        const { profileData, subscriptionData } = await checkSubscriptionFromStripe(currentUser);
        handleDirectRedirect(currentUser, event, profileData, subscriptionData);
      } else {
        setUser(null);
        userIdRef.current = null;
        setProfile(null);
        setSubscription(null);
        setHasRedirectedAfterLogin(false);
      }

      if (mounted) setLoading(false);
    });

    // Fix 1 continued: Then get session — the listener above will handle SIGNED_IN
    // initializeAuth only fetches profile directly from DB (no edge function)
    const initializeAuth = async () => {
      try {
        console.log("AuthContext: Getting initial session");
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;

        const currentUser = session?.user ?? null;
        console.log("AuthContext: Initial user:", currentUser?.email);
        setUser(currentUser);
        userIdRef.current = currentUser?.id ?? null;

        if (currentUser) {
          // Only fetch profile from DB — no edge function call here
          // The onAuthStateChange SIGNED_IN will handle subscription check
          await fetchProfile(currentUser.id);
          // Set a basic subscription from profile data so UI isn't blocked
          const profileData = profile; // may not be set yet due to async
          // Re-fetch to get the data we just set
          const { data: profileRow } = await supabase
            .from('profiles')
            .select('subscription_active')
            .eq('id', currentUser.id)
            .single();
          if (mounted && profileRow) {
            setSubscription({
              subscribed: profileRow.subscription_active || false,
              subscription_tier: profileRow.subscription_active ? 'Premium' : null,
              subscription_end: null,
              payment_provider: 'stripe',
              cancel_at_period_end: false,
            });
          }
        }

        if (mounted) setLoading(false);
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    // Gracefully refresh session when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("AuthContext: Tab became visible, refreshing session");
        supabase.auth.getSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log("AuthContext: useEffect cleanup");
      mounted = false;
      clearTimeout(safetyTimeout);
      authSubscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fix 4: Resilient signOut — clear state immediately, timeout the API call
  const signOut = async () => {
    // Clear local state immediately so UI updates right away
    setUser(null);
    setProfile(null);
    setSubscription(null);
    setHasRedirectedAfterLogin(false);
    userIdRef.current = null;

    // Then tell Supabase to sign out (with 5s timeout)
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))
      ]);
    } catch {
      console.warn('AuthContext: Sign out timed out or failed, cleared local state');
    }
  };

  const value = {
    user,
    profile,
    subscription,
    loading,
    signOut,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
